import { useEffect, useState } from 'react'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import type { CronStatus } from '../types'

const LABEL_CLASS = 'text-xs font-semibold uppercase tracking-wide text-slate-500'
const INPUT_CLASS =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm focus:border-[#1597ff] focus:outline-none focus:ring-2 focus:ring-[#1597ff]/15'

export type CronFrequencyFilter = '' | 'daily' | 'weekly' | 'monthly'

export type CronJobsFiltersState = {
  cron_id: string
  cron_name: string
  cron_status: CronStatus | ''
  frequency: CronFrequencyFilter
}

export const DEFAULT_CRON_JOBS_FILTERS: CronJobsFiltersState = {
  cron_id: '',
  cron_name: '',
  cron_status: '',
  frequency: '',
}

export function countActiveCronJobsFilters(filters: CronJobsFiltersState): number {
  let count = 0
  if (filters.cron_id.trim()) count += 1
  if (filters.cron_name.trim()) count += 1
  if (filters.cron_status) count += 1
  if (filters.frequency) count += 1
  return count
}

type CronJobsFiltersProps = {
  filters: CronJobsFiltersState
  isOpen: boolean
  onClose: () => void
  onApply: (filters: CronJobsFiltersState) => void
  onReset: () => void
}

export function CronJobsFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: CronJobsFiltersProps) {
  const [draft, setDraft] = useState<CronJobsFiltersState>(filters)

  useEffect(() => {
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={() => {
        setDraft({ ...DEFAULT_CRON_JOBS_FILTERS })
        onReset()
      }}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix="cron-jobs"
    >
      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="cron-jobs-filter-cron-id">
          Cron ID
        </label>
        <input
          id="cron-jobs-filter-cron-id"
          type="text"
          value={draft.cron_id}
          onChange={(event) => setDraft((prev) => ({ ...prev, cron_id: event.target.value }))}
          placeholder="Filter by cron ID"
          className={INPUT_CLASS}
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="cron-jobs-filter-cron-name">
          Cron Name
        </label>
        <input
          id="cron-jobs-filter-cron-name"
          type="text"
          value={draft.cron_name}
          onChange={(event) => setDraft((prev) => ({ ...prev, cron_name: event.target.value }))}
          placeholder="Filter by cron name"
          className={INPUT_CLASS}
        />
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="cron-jobs-filter-status">
          Status
        </label>
        <select
          id="cron-jobs-filter-status"
          value={draft.cron_status}
          onChange={(event) =>
            setDraft((prev) => ({
              ...prev,
              cron_status: event.target.value as CronStatus | '',
            }))
          }
          className={INPUT_CLASS}
        >
          <option value="">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className={LABEL_CLASS} htmlFor="cron-jobs-filter-frequency">
          Frequency
        </label>
        <select
          id="cron-jobs-filter-frequency"
          value={draft.frequency}
          onChange={(event) =>
            setDraft((prev) => ({
              ...prev,
              frequency: event.target.value as CronFrequencyFilter,
            }))
          }
          className={INPUT_CLASS}
        >
          <option value="">All</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
    </ListFilterDrawer>
  )
}
